import { EMBARGO_MESSAGE_KEYS, EMBARGO_VALIDATION_TYPES } from './constants';
import { getEmbargoBannerTitle, getEmbargoMessage } from './content';
import type { EmbargoMode, EmbargoSaveError, EmbargoViolation } from './types';

export type ProCardEmbargoItems = {
  violations: EmbargoViolation[];
  warnings: EmbargoViolation[];
};

const isFreezableAdvisory = (violation: EmbargoViolation): boolean =>
  violation.messageKey === EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_ZIP ||
  violation.messageKey === EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_TERMINAL;

/** Freezable zip/terminal warnings (used by Process Pickup card layout). */
export const getFreezableGridWarnings = (
  warnings: EmbargoViolation[] | null | undefined
): EmbargoViolation[] => (warnings ?? []).filter(isFreezableAdvisory);

export const getNonFreezableShipmentEmbargoItems = ({
  violations = [],
  warnings = [],
}: {
  violations?: EmbargoViolation[];
  warnings?: EmbargoViolation[];
}): ProCardEmbargoItems => ({
  violations: violations.filter((violation) => !isFreezableAdvisory(violation)),
  warnings: warnings.filter((warning) => !isFreezableAdvisory(warning)),
});

const dedupeEmbargoItemsByRule = (
  items: EmbargoViolation[]
): EmbargoViolation[] => {
  const seenRuleKeys = new Set<string>();
  return items.filter((item) => {
    const ruleKey = `${item.ruleId}-${item.validationType}-${item.messageKey}`;
    if (seenRuleKeys.has(ruleKey)) {
      return false;
    }
    seenRuleKeys.add(ruleKey);
    return true;
  });
};

/** Client embargo items for the top of Pickup Management modal (deduped by rule). */
export const getPickupManagementTopEmbargoItems = ({
  violations = [],
  warnings = [],
}: {
  violations?: EmbargoViolation[];
  warnings?: EmbargoViolation[];
}): ProCardEmbargoItems => ({
  violations: dedupeEmbargoItemsByRule(violations),
  warnings: dedupeEmbargoItemsByRule(warnings),
});

export const getProCardEmbargoViolations = (
  embargoBlock: {
    shipmentViolations?: EmbargoViolation[];
    shipmentWarnings?: EmbargoViolation[];
  } | null | undefined
): ProCardEmbargoItems => {
  if (!embargoBlock) {
    return { violations: [], warnings: [] };
  }

  return {
    violations: embargoBlock.shipmentViolations ?? [],
    warnings: embargoBlock.shipmentWarnings ?? [],
  };
};

export const filterEmbargoViolationsForExpandedShipments = (
  violations: EmbargoViolation[],
  expandedShipmentIds: string[]
): EmbargoViolation[] => {
  if (!expandedShipmentIds.length) {
    return violations;
  }
  const expandedIds = new Set(expandedShipmentIds);
  return violations.filter(
    (violation) =>
      !violation.shipmentRowId || !expandedIds.has(violation.shipmentRowId)
  );
};

export type EmbargoBannerViewModel = {
  title: string;
  message: string;
  validationType: typeof EMBARGO_VALIDATION_TYPES.ERROR | typeof EMBARGO_VALIDATION_TYPES.WARNING;
};

export const getEmbargoBannerViewModel = (
  mode: EmbargoMode,
  violation: EmbargoViolation | null | undefined
): EmbargoBannerViewModel | null => {
  if (!violation) {
    return null;
  }
  const validationType =
    violation.validationType ?? EMBARGO_VALIDATION_TYPES.ERROR;
  return {
    title: getEmbargoBannerTitle(mode, validationType),
    message: getEmbargoMessage(violation.messageKey, mode),
    validationType,
  };
};

export const getEmbargoSaveErrorBannerViewModel = (
  saveError: EmbargoSaveError | null | undefined
): EmbargoBannerViewModel | null => {
  if (!saveError) {
    return null;
  }
  return {
    title: saveError.embargoTitle,
    message: saveError.embargoDescription,
    validationType: saveError.validationType,
  };
};

export const isEmbargoBlockingConfirm = ({
  shipmentViolations,
  backendSaveError,
}: {
  shipmentViolations?: EmbargoViolation[] | null;
  backendSaveError?: EmbargoSaveError | null;
}): boolean =>
  Boolean(
    shipmentViolations?.some(
      (violation) => violation.validationType === EMBARGO_VALIDATION_TYPES.ERROR
    )
  ) || backendSaveError?.validationType === EMBARGO_VALIDATION_TYPES.ERROR;
